import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { SuccessIcon } from '@/common/icons/general.tsx'
import { Button, buttonVariants } from '@/common/components/Button.tsx'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/common/components/Card'
import { format, parseISO } from 'date-fns'
import { useTypesafeStudent } from './useTypesafeStudent'
import ApplicationStatusBadge from '@/app/common/components/StatusBadge'
import { useEffect, useRef } from 'react'
import { LogUserActivity } from '@/apis/users/requests'
import { LOG_TYPES } from '../constants'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/storage/store'
import { setSelectedStudent } from '@/storage/applications/familyApplicationSlice'

const StudentSuccessVerified = () => {
  const { t: translate } = useTranslation()
  const navigateTo = useNavigate()
  const { student: selectedStudent } = useTypesafeStudent()
  const dispatch = useDispatch<AppDispatch>()

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Student details Summary',
      }) // Log page view
      hasLoggedPageView.current = true // Mark as executed
    }
  }, [])

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1 className="text-3xl font-medium text-gray-900">
          {translate('student_detail_completed')}
        </h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="items-center justify-center">
        <SuccessIcon className="h-24 w-24 text-green-700 mb-4" />
        <h3 className="text-xl font-medium text-center text-gray-800 mb-8">
          {translate('thank_you_for_providing_information')}
        </h3>
        <Card className="w-full flex-shrink-0">
          <CardHeader className="font-medium text-md">
            <div className="flex items-center justify-between">
              {translate('information')}
              <Link
                className={buttonVariants({ variant: 'outline' })}
                onClick={() => {
                  dispatch(setSelectedStudent(selectedStudent.id))
                }}
                to={
                  AppRoutePaths.family_application.student_details.enter_details
                }
              >
                {translate('edit_applicant_details')}
              </Link>
            </div>
          </CardHeader>
          <CardContent className="w-full">
            <ul>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">
                  {translate('application_status')}
                </span>
                <span className="text-neutral-900 font-medium">
                  <ApplicationStatusBadge
                    status={selectedStudent.application_status}
                  />
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">
                  {translate('first_name')}
                </span>
                <span className="text-neutral-900 font-medium">
                  {selectedStudent.first_name}{' '}
                </span>
              </li>
              {selectedStudent.middle_name && (
                <li className="flex items-center justify-between py-2">
                  <span className="text-neutral-700">
                    {translate('middle_name')}
                  </span>
                  <span className="text-neutral-900 font-medium">
                    {selectedStudent.middle_name}
                  </span>
                </li>
              )}
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">
                  {translate('last_name')}
                </span>
                <span className="text-neutral-900 font-medium">
                  {selectedStudent.last_name}
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">
                  {translate('birth_date')}
                </span>
                <span className="text-neutral-900 font-medium">
                  {selectedStudent.date_of_birth
                    ? format(parseISO(selectedStudent.date_of_birth), 'MM-dd-y')
                    : ''}
                </span>
              </li>
              {selectedStudent.SSID && (
                <li className="flex items-center justify-between py-2">
                  <span className="text-neutral-700">SSID</span>
                  <span className="text-neutral-900 font-medium">
                    {selectedStudent.SSID}
                  </span>
                </li>
              )}
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">
                  {translate('relationship')}
                </span>
                <span className="text-neutral-900 font-medium">
                  {translate(
                    (selectedStudent.relationship ?? '').replace(' ', '_')
                  )}
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <section
          className="w-full flex flex-col text-center"
          aria-labelledby="continue_button_title"
          aria-describedby="continue_button_desc"
        >
          <h2 id="continue_button_title" className="sr-only">
            {translate('accessibility.common.continue_button.title')}
          </h2>
          <h2 id="continue_button_desc" className="sr-only">
            {translate('accessibility.common.continue_button.desc')}
          </h2>
          <p className="text-gray-600 mb-2 text-center">
            {translate('up_next_additiona_student')}
          </p>{' '}
          <Button
            onClick={() =>
              navigateTo(
                AppRoutePaths.family_application.student_details
                  .additional_student
              )
            }
            className="h-9 mb-5"
          >
            {translate('continue')}
          </Button>
        </section>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
export default StudentSuccessVerified
